import { useEffect, useCallback } from 'react';
import { Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { VideoPlayerControl } from './VideoPlayerControl';
import { useLazyGetInterviewTranscriptQuery } from '@/store/query/interviews';
import {
  selectCurrentTime,
  selectIsPIPMode,
  selectIsVideoModalOpen,
} from '@/store/interview-video/selectors';
import {
  updateTimeToJump,
  setToPlay,
  setVideoModalOpen,
} from '@/store/interview-video';
import './index.css';
import { KModal } from '@/ui-library/components/molecules/KModal/KModal';
import { InterviewType } from '../types/interview';
import { ModalVideoPlayer } from './ModalVideoPlayer';
import { InterviewTranscriptPanel } from './InterviewTranscriptPanel';

interface InterviewTranscriptProps {
  interview: InterviewType;
  interviewId?: string | undefined | number;
}

export const InterviewTranscript = ({
  interview,
  interviewId: interviewIdProps,
}: InterviewTranscriptProps) => {
  const dispatch = useDispatch();
  const currentTime = useSelector(selectCurrentTime);
  const isPIPMode = useSelector(selectIsPIPMode);
  const isVideoModalOpen = useSelector(selectIsVideoModalOpen);
  const { interviewId: interviewIdFromParams } = useParams<{
    interviewId: string;
  }>();
  const [
    getInterviewTranscript,
    { data, isFetching, isLoading, isUninitialized },
  ] = useLazyGetInterviewTranscriptQuery();

  const interviewId = interviewIdProps || interviewIdFromParams;

  const { recording_url } = interview || {};

  // Show loader on initial load before query is triggered or while fetching
  const showLoader = isUninitialized || isFetching || isLoading;

  useEffect(() => {
    if (interviewId) {
      getInterviewTranscript({ interviewId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  const handleTimeJump = useCallback((timeInMs: number, toPlay = false) => {
    console.log('timeInMs', timeInMs, toPlay);
    dispatch(updateTimeToJump(timeInMs));
    dispatch(setToPlay(toPlay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      flexDir="column"
      height="100%"
      overflow="hidden"
      position="relative"
      data-testid="interview-transcript"
    >
      <Flex flex="1" minH="0" overflow="hidden">
        <InterviewTranscriptPanel
          data={data}
          currentTime={currentTime}
          handleTimeJump={handleTimeJump}
          isLoading={showLoader}
        />
      </Flex>
      {recording_url && !isVideoModalOpen && (
        <Flex
          display={isPIPMode ? 'none' : 'flex'}
          minH="110px"
          borderTop="1px solid #EAEAEA"
        >
          <VideoPlayerControl
            videoUrl={recording_url}
            onMaximize={() => dispatch(setVideoModalOpen(true))}
            mode="mini"
          />
        </Flex>
      )}
      <KModal
        isOpen={isVideoModalOpen}
        onClose={() => dispatch(setVideoModalOpen(false))}
        size="6xl"
        title={null}
        footer={false}
        closeOnOverlayClick
        hideCloseButton={true}
        modalContentProps={{
          style: {
            height: '85vh',
            maxHeight: '85vh',
            width: '95vw',
            maxWidth: '95vw',
            position: 'fixed',
            border: 'none',
            borderRadius: '12px',
          },
        }}
        modalHeaderProps={{ display: 'none' }}
        modalBodyProps={{ maxH: '100%', padding: 0 }}
      >
        <ModalVideoPlayer
          videoUrl={recording_url}
          transcriptData={data}
          currentTime={currentTime}
          handleTimeJump={handleTimeJump}
          interview={interview}
          isLoading={showLoader}
          onClose={() => dispatch(setVideoModalOpen(false))}
        />
      </KModal>
    </Flex>
  );
};
